/**
 * WorkflowsSidebar Component
 * Shows scheduled workflows for an agent with tab support
 */

import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { scheduledWorkflowsService } from '../../services/scheduled-workflows.service';
import type { ScheduledWorkflow } from '../../services/scheduled-workflows.service';
import { useScopeContext } from '../../contexts/ScopeContext';
import { WorkflowStatusBadge } from './WorkflowStatusBadge';

interface WorkflowsSidebarProps {
  agentId: string;
  agentName?: string;
  agentRole?: string;
  currentWorkflowId?: string;
  refreshTrigger?: number;
  onNewWorkflow?: () => void;
}

export function WorkflowsSidebar({
  agentId,
  agentName: _agentName,
  agentRole: _agentRole,
  currentWorkflowId,
  refreshTrigger,
  onNewWorkflow,
}: WorkflowsSidebarProps) {
  const scope = useScopeContext();
  const [workflows, setWorkflows] = useState<ScheduledWorkflow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWorkflows();
  }, [agentId, scope.teamId]);

  // Refresh when trigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      loadWorkflows();
    }
  }, [refreshTrigger]);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      console.log('[WorkflowsSidebar] Loading workflows for agent:', agentId);
      const allWorkflows = await scheduledWorkflowsService.listWorkflows(scope.teamId);
      // Filter workflows for this agent
      const agentWorkflows = allWorkflows.filter(w => w.agentId === agentId);
      console.log('[WorkflowsSidebar] Loaded workflows:', agentWorkflows.length);
      setWorkflows(agentWorkflows);
    } catch (err) {
      console.error('[WorkflowsSidebar] Failed to load workflows:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNewWorkflow = () => {
    if (onNewWorkflow) {
      onNewWorkflow();
    } else {
      // Fallback to navigation if handler not provided
      route(`${scope.getNavigationPrefix()}/workflows/${agentId}/new`);
    }
  };

  const handleWorkflowClick = (workflowId: string) => {
    route(`${scope.getNavigationPrefix()}/workflows/${agentId}/${workflowId}`);
  };

  const formatNextRun = (nextRun?: string) => {
    if (!nextRun) return 'Not scheduled';
    const date = new Date(nextRun);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      return `in ${diffMins}m`;
    } else if (diffHours < 24) {
      return `in ${diffHours}h`;
    } else if (diffDays < 7) {
      return `in ${diffDays}d`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const WorkflowItemComponent = ({ workflow }: { workflow: ScheduledWorkflow }) => (
    <li>
      <div
        class={`flex flex-col gap-2 px-3 py-2 rounded-lg hover:bg-base-content/10 transition-colors cursor-pointer ${
          currentWorkflowId === workflow.workflowId ? 'bg-base-content/15' : ''
        }`}
        onClick={() => handleWorkflowClick(workflow.workflowId)}
      >
        <div class="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span class="truncate flex-1">{workflow.name}</span>
          <WorkflowStatusBadge status={workflow.status} />
        </div>

        {workflow.nextRun && workflow.type === 'repeating' && (
          <div class="text-xs opacity-70 pl-6">
            Next: {formatNextRun(workflow.nextRun)}
          </div>
        )}

        {workflow.type === 'one-off' && workflow.schedule.executeAt && (
          <div class="text-xs opacity-70 pl-6">
            Runs: {new Date(workflow.schedule.executeAt).toLocaleDateString()}
          </div>
        )}
      </div>
    </li>
  );

  // Group workflows by status
  const activeWorkflows = workflows.filter(w => w.status === 'active');
  const pausedWorkflows = workflows.filter(w => w.status === 'paused');
  const otherWorkflows = workflows.filter(w => w.status !== 'active' && w.status !== 'paused');

  return (
    <div class="flex flex-col h-full flex-1 text-sm">
      {/* New Workflow Button */}
      <div class="p-4 border-b border-base-content/10">
        <button
          onClick={handleNewWorkflow}
          class="btn btn-block btn-primary"
        >
          + New Workflow
        </button>
      </div>

      {/* Workflows List */}
      <div class="flex-1 overflow-y-auto px-4 pb-4">
        <h3 class="text-sm font-semibold mb-3 px-2 opacity-80">Workflows</h3>

        {loading ? (
          <div class="flex justify-center py-8">
            <span class="loading loading-spinner loading-md"></span>
          </div>
        ) : (
          <div class="space-y-4">
            {/* Active Workflows */}
            {activeWorkflows.length > 0 && (
              <div>
                <h4 class="text-xs font-semibold mb-2 px-2 opacity-60">Active</h4>
                <ul class="space-y-1">
                  {activeWorkflows.map(workflow => (
                    <WorkflowItemComponent key={workflow.workflowId} workflow={workflow} />
                  ))}
                </ul>
              </div>
            )}

            {/* Paused Workflows */}
            {pausedWorkflows.length > 0 && (
              <div>
                <h4 class="text-xs font-semibold mb-2 px-2 opacity-60">Paused</h4>
                <ul class="space-y-1">
                  {pausedWorkflows.map(workflow => (
                    <WorkflowItemComponent key={workflow.workflowId} workflow={workflow} />
                  ))}
                </ul>
              </div>
            )}

            {/* Other (error, completed) */}
            {otherWorkflows.length > 0 && (
              <div>
                <h4 class="text-xs font-semibold mb-2 px-2 opacity-60">Other</h4>
                <ul class="space-y-1">
                  {otherWorkflows.map(workflow => (
                    <WorkflowItemComponent key={workflow.workflowId} workflow={workflow} />
                  ))}
                </ul>
              </div>
            )}

            {workflows.length === 0 && !loading && (
              <div class="text-center py-8 text-base-content/60">
                <p class="text-sm">No workflows yet</p>
                <p class="text-xs mt-1">Create one above</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
